import React, { useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import './ProviderAlertsBanner.css';

// Wie oft im Hintergrund neu geprüft wird (ms).
const POLL_INTERVAL_MS = 90000;

function formatWhen(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('de-DE', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
    });
}

/**
 * Zeigt oben im Team-Dashboard ein Warning, sobald in den API-Antworten oder
 * Logs erkannt wurde, dass bei einem unserer Anbieter (OpenAI, Claude,
 * Mailivery, WarmupInbox, Sistrix, …) das Guthaben leer ist oder eine Zahlung
 * fehlt. Die Daten kommen aus getProviderAlertsAdmin; quittieren über
 * dismissProviderAlertAdmin.
 */
function ProviderAlertsBanner() {
    const [alerts, setAlerts] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [dismissing, setDismissing] = useState(null);

    const load = useCallback(async () => {
        try {
            const fn = httpsCallable(functions, 'getProviderAlertsAdmin');
            const res = await fn({});
            const list = (res.data && res.data.alerts) || [];
            setAlerts(list.filter((a) => a.active));
        } catch (err) {
            // Banner ist optional — Fehler still schlucken, Dashboard nicht stören.
            console.warn('[ProviderAlerts] load failed:', err.message);
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(t);
    }, [load]);

    const dismiss = useCallback(async (provider) => {
        setDismissing(provider);
        try {
            const fn = httpsCallable(functions, 'dismissProviderAlertAdmin');
            await fn({ provider });
            setAlerts((prev) => prev.filter((a) => a.provider !== provider));
        } catch (err) {
            console.warn('[ProviderAlerts] dismiss failed:', err.message);
        } finally {
            setDismissing(null);
        }
    }, []);

    if (!loaded || alerts.length === 0) return null;

    return (
        <div className="provider-alerts-banner">
            {alerts.map((a) => (
                <div key={a.provider} className="provider-alert-card">
                    <div className="provider-alert-icon">
                        <i className="fa fa-exclamation-triangle"></i>
                    </div>
                    <div className="provider-alert-body">
                        <div className="provider-alert-title">
                            Bitte aufladen: <strong>{a.label}</strong>
                            {a.count > 1 && (
                                <span className="provider-alert-count">{a.count}× aufgetreten</span>
                            )}
                        </div>
                        <div className="provider-alert-meta">
                            {a.host ? `${a.host} · ` : ''}
                            {a.statusCode ? `HTTP ${a.statusCode} · ` : ''}
                            Zuletzt {formatWhen(a.lastSeenAt)}
                            {a.firstSeenAt && a.firstSeenAt !== a.lastSeenAt
                                ? ` · Erstmals ${formatWhen(a.firstSeenAt)}`
                                : ''}
                        </div>
                        {a.message && (
                            <div className="provider-alert-message" title={a.message}>
                                {a.message}
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="provider-alert-dismiss"
                        onClick={() => dismiss(a.provider)}
                        disabled={dismissing === a.provider}
                        title="Als erledigt markieren (z. B. nach dem Aufladen)"
                    >
                        {dismissing === a.provider ? '…' : 'Erledigt'}
                    </button>
                </div>
            ))}
        </div>
    );
}

export default ProviderAlertsBanner;
